import type { AliasableExpression, QueryCreator } from 'kysely';
import { uniqueId } from 'lodash-es';
import groupBy from 'lodash/groupBy';
import type { RequestContainer } from '$lib/container/container.server';
import type { CurrentParticipantInput } from '$lib/gql/resolver-types';
import type { DB } from '$lib/kysely/db-types.js';
import {
    equalOrIsNull,
    nullOrInListWhenNonEmptyList,
    valOrNull,
} from '$lib/kysely/kysely-utils.js';
import { notNullableOrThrow } from '$lib/utils/assert';
import {
    matchCurrentParticipantInput,
    matchParticipantRef,
    participantInputAreEqual,
    type ParticipantRef,
    participantRefToInput,
} from '$gql/resolvers/ParticipantRef.js';
import type { InboxContextRef } from './InboxContextRef';

const ID = uniqueId('inbox-queries');

export async function getParticipants(
    {
        threadId,
        withCache = true,
    }: {
        threadId: number;
        withCache?: boolean;
    },
    context: Pick<RequestContainer, 'db' | 'dataLoaderRegistry' | 'refs'>,
) {
    const { db, dataLoaderRegistry, refs } = context;

    const loader = dataLoaderRegistry().createForRequest(
        `${ID}-participants`,
        async (ids: readonly number[]) => {
            const userIds = await db()
                .selectFrom('inbox_thread_user as tu')
                .select(['tu.inbox_thread_id as thread_id', 'tu.user_id as user_id'])
                .where('tu.inbox_thread_id', 'in', ids)
                .execute();

            const companyIds = await db()
                .selectFrom('inbox_thread_company as tc')
                .select([
                    'tc.inbox_thread_id as thread_id',
                    'tc.company_id as company_id',
                ])
                .where('tc.inbox_thread_id', 'in', ids)
                .execute();

            const userIdsByThreadId = groupBy(userIds, (r) => r.thread_id);
            const companyIdsIdByThreadId = groupBy(companyIds, (r) => r.thread_id);

            return ids.map((id) => {
                const users = userIdsByThreadId[id] ?? [];
                const companies = companyIdsIdByThreadId[id] ?? [];

                return [
                    ...users.map((r) => refs().user(r.user_id)),
                    ...companies.map((r) => refs().company(r.company_id)),
                ];
            });
        },
    );

    if (!withCache) {
        loader.clear(threadId);
    }

    return await loader.load(threadId);
}

export async function getParticipantsWithoutMe(
    {
        threadId,
        inboxContext,
        withCache,
    }: {
        threadId: number;
        withCache?: boolean;
        inboxContext: InboxContextRef;
    },
    context: Pick<
        RequestContainer,
        'db' | 'dataLoaderRegistry' | 'refs' | 'securityManager'
    >,
) {
    const { userIds, companyIds } = await inboxContext.getCurrentParticipantIds();
    const participants = await getParticipants({ threadId, withCache }, context);
    let filtered = participants.filter((p) =>
        matchCurrentParticipantInput(inboxContext.currentParticipant, {
            user: (userId) =>
                !participantInputAreEqual(
                    { userId, companyId: null },
                    participantRefToInput(p),
                ),
            company: (companyId) =>
                !participantInputAreEqual(
                    { userId: null, companyId },
                    participantRefToInput(p),
                ),
            all: (_all) =>
                matchParticipantRef(p, {
                    user: (user) => !userIds.includes(user.id()),
                    company: (company) => !companyIds.includes(company.id()),
                }),
        }),
    );
    if (
        filtered.length === 0 &&
        participants.length > 0 &&
        inboxContext.currentParticipant.all
    ) {
        const userId = await context.securityManager().currentUserIdOrNull();

        let currentUserParticipant =
            userId == null
                ? null
                : participants.find((p) =>
                      participantInputAreEqual(
                          { userId, companyId: null },
                          participantRefToInput(p),
                      ),
                  );

        filtered = participants.filter(
            (p) =>
                !participantInputAreEqual(
                    participantRefToInput(
                        currentUserParticipant ?? notNullableOrThrow(participants[0]),
                    ),
                    participantRefToInput(p),
                ),
        );
    }

    return filtered;
}

export function threadParticipantQuery(db: QueryCreator<DB>) {
    return db
        .selectFrom('inbox_thread_company')
        .select(({ ref, lit }) => [
            ref('inbox_thread_id').as('thread_id'),
            lit<number | null>(null).as('user_id'),
            (ref('company_id') as AliasableExpression<number | null>).as('company_id'),
        ])
        .union(
            db
                .selectFrom('inbox_thread_user')
                .select(({ ref, lit }) => [
                    ref('inbox_thread_id').as('thread_id'),
                    (ref('user_id') as AliasableExpression<number | null>).as('user_id'),
                    lit<number | null>(null).as('company_id'),
                ]),
        )
        .$assertType<{
            thread_id: number;
            user_id: number | null;
            company_id: number | null;
        }>();
}

export function myThreadQuery(me: ParticipantRef, db: QueryCreator<DB>) {
    const { userId = null, companyId = null } = participantRefToInput(me);

    return db
        .with('thread_participant(thread_id, user_id, company_id)', (db) =>
            db
                .selectFrom(threadParticipantQuery(db).as('tp'))
                .select(['thread_id', 'user_id', 'company_id']),
        )
        .selectFrom('thread_participant')
        .select('thread_participant.thread_id')
        .where(({ ref, and }) =>
            and([
                equalOrIsNull(ref('thread_participant.user_id'), userId),
                equalOrIsNull(ref('thread_participant.company_id'), companyId),
            ]),
        );
}

export function participantAuthorizedByMeQuery(me: ParticipantRef, db: QueryCreator<DB>) {
    const { userId = null, companyId = null } = participantRefToInput(me);

    return db
        .selectFrom('user_inbox_authorized_user')
        .where(({ ref }) => equalOrIsNull(ref('user_source'), userId))
        .where(({ eb }) =>
            eb(valOrNull(userId), 'is', null).or(eb('user_target', '!=', userId)),
        )
        .select(({ ref, lit }) => [
            (ref('user_target') as AliasableExpression<number | null>).as('user_id'),
            lit<number | null>(null).as('company_id'),
            (ref('user_source') as AliasableExpression<number | null>).as('by_user_id'),
            lit<number | null>(null).as('by_company_id'),
        ])
        .union(
            db
                .selectFrom('user_inbox_authorized_company')
                .where(({ ref }) => equalOrIsNull(ref('user_id'), userId))
                .select(({ ref, lit }) => [
                    lit<number | null>(null).as('user_id'),
                    ref('company_id').as('company_id'),
                    (ref('user_id') as AliasableExpression<number | null>).as(
                        'by_user_id',
                    ),
                    lit<number | null>(null).as('by_company_id'),
                ]),
        )
        .union(
            db
                .selectFrom('company_inbox_authorized_company')
                .where(({ ref }) => equalOrIsNull(ref('company_source'), companyId))
                .where(({ eb }) =>
                    eb(valOrNull(userId), 'is', null).or(
                        eb('company_target', '!=', companyId),
                    ),
                )
                .select(({ ref, lit }) => [
                    lit<number | null>(null).as('user_id'),
                    (ref('company_target') as AliasableExpression<number | null>).as(
                        'company_id',
                    ),
                    lit<number | null>(null).as('by_user_id'),
                    (ref('company_source') as AliasableExpression<number | null>).as(
                        'by_company_id',
                    ),
                ]),
        )
        .union(
            db
                .selectFrom('company_inbox_authorized_user')
                .where(({ ref }) => equalOrIsNull(ref('company_id'), companyId))
                .select(({ ref, lit }) => [
                    (ref('user_id') as AliasableExpression<number | null>).as('user_id'),
                    lit<number | null>(null).as('company_id'),
                    lit<number | null>(null).as('by_user_id'),
                    (ref('company_id') as AliasableExpression<number | null>).as(
                        'by_company_id',
                    ),
                ]),
        )
        .$assertType<{
            user_id: number | null;
            company_id: number | null;
            by_user_id: number | null;
            by_company_id: number | null;
        }>();
}

export function participantAuthorizedByAllQuery(
    _currentParticipant: CurrentParticipantInput,
    userIds: number[],
    companyIds: number[],
    db: QueryCreator<DB>,
) {
    // source: the authorizer, check all sources
    // target: the authorized participant, we need to get the target

    return (
        db
            .selectFrom('user_inbox_authorized_user')
            //user_source and user_target are not null
            //.where(({ ref }) => equalOrIsNull(ref('user_source'), userId))
            .where(({ ref }) => nullOrInListWhenNonEmptyList(ref('user_source'), userIds))
            .where(({ eb, ref }) =>
                //eb(valOrNull(userId), 'is', null).or(eb('user_target', '!=', userId)),
                eb(ref('user_source'), '!=', ref('user_target')),
            )
            .select(({ ref, lit }) => [
                (ref('user_target') as AliasableExpression<number | null>).as('user_id'),
                lit<number | null>(null).as('company_id'),
                (ref('user_source') as AliasableExpression<number | null>).as(
                    'by_user_id',
                ),
                lit<number | null>(null).as('by_company_id'),
            ])
            .union(
                db
                    .selectFrom('user_inbox_authorized_company')
                    //.where(({ ref }) => equalOrIsNull(ref('user_id'), userId))
                    .where(({ ref }) =>
                        nullOrInListWhenNonEmptyList(ref('user_id'), userIds),
                    )
                    .select(({ ref, lit }) => [
                        lit<number | null>(null).as('user_id'),
                        ref('company_id').as('company_id'),
                        (ref('user_id') as AliasableExpression<number | null>).as(
                            'by_user_id',
                        ),
                        lit<number | null>(null).as('by_company_id'),
                    ]),
            )
            .union(
                db
                    .selectFrom('company_inbox_authorized_company')
                    //.where(({ ref }) => equalOrIsNull(ref('company_source'), companyId))
                    .where(({ ref }) =>
                        nullOrInListWhenNonEmptyList(ref('company_source'), companyIds),
                    )
                    .where(({ eb, ref }) =>
                        /* eb(valOrNull(userId), 'is', null).or(
                            eb('company_target', '!=', companyId),
                        ), */
                        eb(ref('company_source'), '!=', ref('company_target')),
                    )
                    .select(({ ref, lit }) => [
                        lit<number | null>(null).as('user_id'),
                        (ref('company_target') as AliasableExpression<number | null>).as(
                            'company_id',
                        ),
                        lit<number | null>(null).as('by_user_id'),
                        (ref('company_source') as AliasableExpression<number | null>).as(
                            'by_company_id',
                        ),
                    ]),
            )
            .union(
                db
                    .selectFrom('company_inbox_authorized_user')
                    //.where(({ ref }) => equalOrIsNull(ref('company_id'), companyId))
                    .where(({ ref }) =>
                        nullOrInListWhenNonEmptyList(ref('company_id'), companyIds),
                    )
                    .select(({ ref, lit }) => [
                        (ref('user_id') as AliasableExpression<number | null>).as(
                            'user_id',
                        ),
                        lit<number | null>(null).as('company_id'),
                        lit<number | null>(null).as('by_user_id'),
                        (ref('company_id') as AliasableExpression<number | null>).as(
                            'by_company_id',
                        ),
                    ]),
            )
            .$assertType<{
                user_id: number | null;
                company_id: number | null;
                by_user_id: number | null;
                by_company_id: number | null;
            }>()
    );
}

export function participantBannedQuery(
    userIds: number[],
    companyIds: number[],
    db: QueryCreator<DB>,
) {
    // source: the banner, check all sources
    // target: the banned participant, we need to get the target

    return db
        .selectFrom('user_inbox_banned_user')
        .where(({ ref }) => nullOrInListWhenNonEmptyList(ref('user_source'), userIds))
        .where(({ eb, ref }) => eb(ref('user_source'), '!=', ref('user_target')))
        .select(({ ref, lit }) => [
            (ref('user_target') as AliasableExpression<number | null>).as('user_id'),
            lit<number | null>(null).as('company_id'),
            (ref('user_source') as AliasableExpression<number | null>).as('by_user_id'),
            lit<number | null>(null).as('by_company_id'),
        ])
        .union(
            db
                .selectFrom('user_inbox_banned_company')
                .where(({ ref }) => nullOrInListWhenNonEmptyList(ref('user_id'), userIds))
                .select(({ ref, lit }) => [
                    lit<number | null>(null).as('user_id'),
                    ref('company_id').as('company_id'),
                    (ref('user_id') as AliasableExpression<number | null>).as(
                        'by_user_id',
                    ),
                    lit<number | null>(null).as('by_company_id'),
                ]),
        )
        .union(
            db
                .selectFrom('company_inbox_banned_company')
                .where(({ ref }) =>
                    nullOrInListWhenNonEmptyList(ref('company_source'), companyIds),
                )
                .where(({ eb, ref }) =>
                    eb(ref('company_source'), '!=', ref('company_target')),
                )
                .select(({ ref, lit }) => [
                    lit<number | null>(null).as('user_id'),
                    (ref('company_target') as AliasableExpression<number | null>).as(
                        'company_id',
                    ),
                    lit<number | null>(null).as('by_user_id'),
                    (ref('company_source') as AliasableExpression<number | null>).as(
                        'by_company_id',
                    ),
                ]),
        )
        .union(
            db
                .selectFrom('company_inbox_banned_user')
                .where(({ ref }) =>
                    nullOrInListWhenNonEmptyList(ref('company_id'), companyIds),
                )
                .select(({ ref, lit }) => [
                    (ref('user_id') as AliasableExpression<number | null>).as('user_id'),
                    lit<number | null>(null).as('company_id'),
                    lit<number | null>(null).as('by_user_id'),
                    (ref('company_id') as AliasableExpression<number | null>).as(
                        'by_company_id',
                    ),
                ]),
        )
        .$assertType<{
            user_id: number | null;
            company_id: number | null;
            by_user_id: number | null;
            by_company_id: number | null;
        }>();
}
